import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from '../../common.js';
/**
 * Check-in module (ROADMAP §1.3 `checkins`, §2 Phase 1, design package "Screen F").
 *
 * The hyper-mini family social surface: a photo, an optional one-liner, an optional
 * per-post place. The only social signal is "seen by" — there are deliberately no likes,
 * comments, counts or streaks anywhere in this contract, and no field that could grow into
 * one. If a future schema change would let the UI render a number next to a post, it is
 * the wrong change.
 *
 * Split of responsibilities:
 *  - **Yjs** owns every piece of metadata below (posts, seen-by stamps). All collaborative
 *    writes go through the `checkins` doc; no REST endpoint mutates it.
 *  - **Photos are not in Yjs.** The client downscales + EXIF-strips a capture, keys it by
 *    the sha256 of the encoded bytes, queues it in an IndexedDB outbox and uploads it to
 *    the server blob store. `photoRef.state` is the only bridge between the two worlds, so
 *    text and place always sync before the photo (design rule).
 */
/** Yjs doc holding `Y.Map<checkinId, Checkin>` (ROADMAP §1.3). */
export const CHECKINS_DOC = 'checkins';
/** Key of the `Y.Map` inside `CHECKINS_DOC` that holds the posts. */
export const CHECKINS_MAP_KEY = 'checkins';
/** Hard cap on the one-liner. The composer counts down from this (design: "140 left"). */
export const CHECKIN_TEXT_MAX = 140;
/**
 * Longest side the client downscales a capture to before encoding. 1600px is plenty for
 * every screen this app actually renders a photo on — the single-photo view included — and
 * roughly halves bytes versus the old 2000px cap.
 */
export const CHECKIN_PHOTO_MAX_DIMENSION = 1600;
/** JPEG quality used by the canvas re-encode (which is also what strips EXIF). */
export const CHECKIN_PHOTO_JPEG_QUALITY = 0.8;
/** Server-side upload cap. A 1600px JPEG at q0.80 lands far below this. */
export const CHECKIN_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
/** Content types the blob store accepts. JPEG is what our own pipeline produces. */
export const CHECKIN_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const checkinPhotoContentTypeSchema = z.enum(CHECKIN_PHOTO_CONTENT_TYPES);
/** Longest side of the server-generated thumbnail (push payloads + archive tiles). */
export const CHECKIN_THUMB_SIZE = 320;
/**
 * Longest side of the server-generated "display" variant: the feed's size. Big enough to
 * fill a phone screen crisply, far smaller than an original — the feed is the highest-
 * traffic surface in the app, so this is the variant that matters most for bytes moved.
 */
export const CHECKIN_DISPLAY_SIZE = 900;
/**
 * A photo id is the lowercase hex sha256 of the *encoded* bytes. Content-addressing gives
 * idempotent uploads for free: a retry after an ambiguous failure is the same PUT.
 */
export const checkinPhotoIdSchema = z.string().regex(/^[0-9a-f]{64}$/, 'expected a sha256 hex id');
/**
 * Where a photo currently lives:
 *  - `local`     — bytes are in this device's outbox only; other members see the
 *                  "photo held on this phone" placeholder.
 *  - `uploading` — an upload attempt is in flight.
 *  - `uploaded`  — the server has the bytes; every client can `GET` it.
 *
 * Only the author's device advances the state, and only forwards.
 */
export const CHECKIN_PHOTO_STATES = ['local', 'uploading', 'uploaded'];
export const checkinPhotoStateSchema = z.enum(CHECKIN_PHOTO_STATES);
export const checkinPhotoRefSchema = z.object({
    id: checkinPhotoIdSchema,
    /** Encoded pixel dimensions, so the feed can reserve space before the bytes arrive. */
    w: z.number().int().min(1).max(20_000),
    h: z.number().int().min(1).max(20_000),
    state: checkinPhotoStateSchema,
});
/**
 * A place is a *user-typed name* with the browser-geolocation coordinates attached when
 * the member granted permission at post time. No geocoding, no map tiles, no lookup
 * service — north-star #7 and the design's location rule both forbid an external call
 * here. Coordinates exist only so a future self-hosted map could plot them.
 */
export const checkinPlaceSchema = z.object({
    name: z.string().trim().min(1).max(120),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
});
/**
 * Per-user "I have seen this" stamps: `{ [userId]: ISO timestamp }`.
 *
 * A record, not a count — the UI renders faces, never a number, and never renders the
 * author's own entry (you cannot "see" your own post). Stamped exactly once per viewer;
 * later views never rewrite the timestamp, so this is a monotonic, conflict-free set.
 */
export const checkinSeenBySchema = z.record(idSchema, isoDateTimeSchema);
export const checkinSchema = z
    .object({
    id: idSchema,
    authorId: idSchema,
    createdAt: isoDateTimeSchema,
    text: z.string().trim().max(CHECKIN_TEXT_MAX).optional(),
    place: checkinPlaceSchema.optional(),
    photoRef: checkinPhotoRefSchema.optional(),
    seenBy: checkinSeenBySchema,
})
    .refine((value) => Boolean(value.photoRef) || Boolean(value.text?.length), {
    message: 'a check-in needs a photo or some text',
});
/**
 * `GET /api/checkin/storage` — drives the archive's "it lives on your own server" line.
 *
 * Both fields describe **originals only**. Thumbnails and the display variant are derived,
 * regenerable from the original at any time, and deliberately excluded from the headline
 * number — otherwise "how much room does this take" would inflate every time we add a new
 * derived size, without the family having taken a single new photo.
 */
export const checkinStorageSchema = z.object({
    /** Original photos held in the blob store. */
    count: z.number().int().min(0),
    /** Bytes of original photo data on disk — derived variants not counted. */
    bytes: z.number().int().min(0),
});
/** `PUT /api/checkin/photo/:id` response. `created: false` means the id was already stored. */
export const checkinPhotoUploadResponseSchema = z.object({
    id: checkinPhotoIdSchema,
    created: z.boolean(),
    bytes: z.number().int().min(0),
});
/**
 * `POST /api/checkin/announce` — the notification *content* for a post the author's client
 * has just written to Yjs.
 *
 * Why this exists: `onDocUpdate` tells the server that `checkins` changed but carries no
 * document content (module code must not reach into the sync layer), and the push body
 * needs the words. So the author's device deposits the snippet and the **server-side doc
 * observer is still the trigger** — nothing is ever sent until the matching Yjs update has
 * actually landed and been persisted. Announcements can therefore arrive before or after
 * the update, which is exactly what an offline author produces.
 */
export const checkinAnnounceRequestSchema = z.object({
    checkinId: idSchema,
    text: z.string().trim().max(CHECKIN_TEXT_MAX).optional(),
    placeName: z.string().trim().max(120).optional(),
    photoId: checkinPhotoIdSchema.optional(),
});
export const checkinAnnounceResponseSchema = z.object({
    /** `pending` = held until the doc update lands; `sent` = the update was already here. */
    status: z.enum(['pending', 'sent', 'duplicate']),
});
/** Auth'd full-size photo URL. Same-origin path — never an absolute URL (north-star #7). */
export function checkinPhotoUrl(id) {
    return `/api/checkin/photo/${id}`;
}
/** Auth'd ~320px thumbnail URL (archive tiles, push payloads). */
export function checkinThumbUrl(id) {
    return `/api/checkin/photo/${id}/thumb`;
}
/** Auth'd ~900px "display" URL (the feed's size). */
export function checkinDisplayUrl(id) {
    return `/api/checkin/photo/${id}/display`;
}
/** Deep link the push notification opens: exactly that photo, nothing else. */
export function checkinPostPath(checkinId) {
    return `/checkin/p/${checkinId}`;
}
/**
 * The photo state machine, in one place so the client and the tests agree.
 * Transitions are forward-only: `local → uploading → uploaded`, plus `uploading → local`
 * when an attempt fails and the entry goes back in the queue.
 */
export function canAdvancePhotoState(from, to) {
    if (from === to)
        return false;
    if (from === 'local')
        return to === 'uploading';
    if (from === 'uploading')
        return to === 'uploaded' || to === 'local';
    return false; // 'uploaded' is terminal
}
/** True when *any* client can fetch the bytes from the server. */
export function isPhotoFetchable(ref) {
    return ref.state === 'uploaded';
}
/**
 * Whether `viewerId` should stamp `seenBy` on this check-in.
 *
 * Two rules, both from the design: you never "see" your own post (so the author's own
 * unseen posts can never become a nag), and a stamp is written exactly once — re-reading
 * the feed must not churn the doc.
 */
export function shouldStampSeen(checkin, viewerId) {
    if (checkin.authorId === viewerId)
        return false;
    return checkin.seenBy[viewerId] === undefined;
}
/** The viewers whose faces the "Seen by" row renders — everyone except the author. */
export function seenByViewers(checkin) {
    return Object.keys(checkin.seenBy).filter((userId) => userId !== checkin.authorId);
}
//# sourceMappingURL=index.js.map